import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Shield,
  Search,
  AlertCircle,
  Save,
  X,
  Trash2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import { formatDateString } from "../CommonJquery/CommonJquery.js";
import {
  get_crm,
  crm_save_update,
  server_post_data,
} from "../ServiceConnection/serviceconnection.js";

export default function CRM() {
  const { toast } = useToast();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [crmData, setCrmData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("Name");

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [formData, setFormData] = useState({
    phone: "",
    name: "",
    email: "",
    dateOfBirth: null,
    dateOfAnniversary: null,
    primaryAddress: "",
    primaryLocality: "",
    creditLimit: "",
    gstNo: "",
    markAsFavorite: false,
    doNotSendUpdates: false,
    tags: "",
  });
  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState("0");

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchCrmData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", "1");
    fd.append("call_id", "0");
    fd.append("search_option", "Name");
    fd.append("search_data", "");
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    fd.append("start_date", sevenDaysAgo.toISOString().split("T")[0] || "");
    fd.append("end_date", new Date().toISOString().split("T")[0] || "");

    try {
      const response = await server_post_data(get_crm, fd);
      if (!response.data.error) {
        const data = response.data.message.data_crm || [];
        setCrmData(data);
        setFilteredData(data);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching CRM data:", error);
      toast({
        title: "Error",
        description: "Failed to load customers",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // ── Search ─────────────────────────────────────────────────────────────────

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      if (!query.trim()) {
        setFilteredData(crmData);
        return;
      }
      const searchLower = query.toLowerCase();
      const results = crmData.filter((customer) => {
        if (searchType === "Name")
          return customer.guest_name?.toLowerCase().includes(searchLower);
        if (searchType === "Email")
          return customer.guest_email?.toLowerCase().includes(searchLower);
        if (searchType === "Phone")
          return customer.guest_mobile_no?.includes(query);
        return true;
      });
      setFilteredData(results);
    },
    [crmData, searchType],
  );

  // ── Form helpers ───────────────────────────────────────────────────────────

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const EMPTY_FORM = {
    phone: "",
    name: "",
    email: "",
    dateOfBirth: null,
    dateOfAnniversary: null,
    primaryAddress: "",
    primaryLocality: "",
    creditLimit: "",
    gstNo: "",
    markAsFavorite: false,
    doNotSendUpdates: false,
    tags: "",
  };

  const handleOpenCreateDialog = useCallback(() => {
    setEditMode(false);
    setEditId("0");
    setFormData(EMPTY_FORM);
    setErrors({});
    setIsCreateDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback(
    async (customer) => {
      setEditMode(true);
      setEditId(customer.primary_id);

      const fd = new FormData();
      fd.append("flag", "3");
      fd.append("call_id", customer.primary_id);

      try {
        const response = await server_post_data(get_crm, fd);
        if (
          !response.data.error &&
          response.data.message.data_crm?.length > 0
        ) {
          const d = response.data.message.data_crm[0];
          setFormData({
            phone: d.guest_mobile_no || "",
            name: d.guest_name || "",
            email: d.guest_email || "",
            dateOfBirth: d.guest_dob || null,
            dateOfAnniversary: d.guest_aniversary || null,
            primaryAddress: d.guest_address || "",
            primaryLocality: d.guest_locality || "",
            creditLimit: d.amount_of_booking_credit || "",
            gstNo: d.guest_gst_no || "",
            markAsFavorite: d.guest_status === 1,
            doNotSendUpdates: d.sms_come_dfoodo === 1,
            tags: d.guest_tags || "",
          });
          setErrors({});
          setIsCreateDialogOpen(true);
        }
      } catch (error) {
        console.error("Error fetching customer data:", error);
        toast({
          title: "Error",
          description: "Failed to load customer data",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  // ── Validation ─────────────────────────────────────────────────────────────

  const validateForm = useCallback(() => {
    const newErrors = {};

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const PHONE_REGEX = /^[6-9]\d{9}$/; // Indian mobile: starts 6-9, exactly 10 digits
    const GST_REGEX =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const NAME_REGEX = /^[a-zA-Z\s.'-]+$/; // letters, spaces, dot, apostrophe, hyphen only

    // Phone
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    else if (!PHONE_REGEX.test(formData.phone))
      newErrors.phone =
        "Enter a valid 10-digit mobile number starting with 6–9";

    // Name
    if (!formData.name.trim()) newErrors.name = "Name is required";
    else if (formData.name.trim().length < 2)
      newErrors.name = "Name must be at least 2 characters";
    else if (formData.name.trim().length > 75)
      newErrors.name = "Name must not exceed 75 characters";
    else if (!NAME_REGEX.test(formData.name.trim()))
      newErrors.name =
        "Name can only contain letters, spaces, dots, hyphens, and apostrophes";

    // Email
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!EMAIL_REGEX.test(formData.email))
      newErrors.email = "Enter a valid email address";

    // Date of Birth — must not be a future date
    if (formData.dateOfBirth) {
      const dob = new Date(formData.dateOfBirth);
      if (dob > today)
        newErrors.dateOfBirth = "Date of birth cannot be a future date";
    }

    // Date of Anniversary — must not be a future date
    if (formData.dateOfAnniversary) {
      const ann = new Date(formData.dateOfAnniversary);
      if (ann > today)
        newErrors.dateOfAnniversary =
          "Anniversary date cannot be a future date";
    }

    // Address
    if (!formData.primaryAddress.trim())
      newErrors.primaryAddress = "Address is required";
    else if (formData.primaryAddress.trim().length < 5)
      newErrors.primaryAddress = "Address must be at least 5 characters";

    // Locality
    if (!formData.primaryLocality.trim())
      newErrors.primaryLocality = "Locality is required";
    else if (formData.primaryLocality.trim().length < 3)
      newErrors.primaryLocality = "Locality must be at least 3 characters";

    // GST No — optional but validated when provided
    if (formData.gstNo.trim() && !GST_REGEX.test(formData.gstNo.trim()))
      newErrors.gstNo = "Enter a valid 15-character GST number";

    // Credit Limit
    if (!formData.creditLimit)
      newErrors.creditLimit = "Credit Limit is required";

    // Tags
    if (!formData.tags) newErrors.tags = "Tag is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("main_id", editId);
    fd.append("guest_mobile_no", formData.phone);
    fd.append("guest_name", formData.name);
    fd.append("guest_email", formData.email);
    fd.append("guest_dob", formData.dateOfBirth || "");
    fd.append("guest_aniversary", formData.dateOfAnniversary || "");
    fd.append("guest_address", formData.primaryAddress);
    fd.append("guest_locality", formData.primaryLocality);
    fd.append("amount_of_booking_credit", formData.creditLimit);
    fd.append("guest_gst_no", formData.gstNo || "");
    fd.append("guest_status", formData.markAsFavorite ? "1" : "0");
    fd.append("sms_come_dfoodo", formData.doNotSendUpdates ? "1" : "0");
    fd.append("guest_tags", formData.tags);

    try {
      const response = await server_post_data(crm_save_update, fd);
      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editMode
            ? "Customer updated successfully"
            : "Customer created successfully",
        });
        setIsCreateDialogOpen(false);
        fetchCrmData();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save customer",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, editId, editMode, validateForm, toast, fetchCrmData]);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDeleteClick = useCallback((customer) => {
    setSelectedCustomer(customer);
    setIsDeleteDialogOpen(true);
  }, []);

  // FIX: actual API call instead of placeholder
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedCustomer) return;

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("main_id", selectedCustomer.primary_id);
    fd.append("flag", "d");

    try {
      const response = await server_post_data(crm_save_update, fd);
      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Customer deleted successfully",
        });
        setIsDeleteDialogOpen(false);
        setSelectedCustomer(null);
        await fetchCrmData(); // FIX: await before clearing loader
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Failed to delete customer",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false); // FIX: always runs after async ops
    }
  }, [selectedCustomer, toast, fetchCrmData]);

  // ── Init ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchCrmData();
  }, [fetchCrmData]);

  const stats = {
    total: crmData.length,
    favorite: crmData.filter((r) => r.guest_status === 1).length,
    vip: crmData.filter((r) => r.guest_tags === "Vip").length,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MainLayout>
      <Header
        title="Customer Management"
        subtitle="Manage and organize your customer database efficiently"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Total Customers", value: stats.total, color: "primary" },
            { label: "Favorite", value: stats.favorite, color: "success" },
            { label: "VIP", value: stats.vip, color: "destructive" },
          ].map(({ label, value, color }) => (
            <div key={label} className="stat-card">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}
                >
                  <Shield className={`w-5 h-5 text-${color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{value}</p>
                  <p className="text-sm text-muted-foreground">{label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={`Search by ${searchType}...`}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Search type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Name">Name</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Phone">Phone</SelectItem>
              </SelectContent>
            </Select>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setFilteredData(crmData);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Button
            onClick={handleOpenCreateDialog}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> Add Customer
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Name</TableHead>
                <TableHead className="text-muted-foreground">Email</TableHead>
                <TableHead className="text-muted-foreground">Phone</TableHead>
                <TableHead className="text-muted-foreground">Tag</TableHead>
                <TableHead className="text-muted-foreground">Created</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground py-8"
                  >
                    No customers found
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((customer) => (
                  <TableRow key={customer.primary_id} className="border-border">
                    <TableCell className="font-semibold">
                      {customer.guest_name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {customer.guest_email}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {customer.guest_mobile_no}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {customer.guest_tags || "Regular"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateString(customer.entry_date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditDialog(customer)}
                          className="hover:bg-primary/10 hover:text-primary"
                          title="Edit Customer"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(customer)}
                          className="hover:bg-destructive/10 hover:text-destructive"
                          title="Delete Customer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create / Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editMode ? "Edit Customer" : "Create New Customer"}
              </DialogTitle>
              <DialogDescription>
                {editMode
                  ? "Update customer information"
                  : "Add a new customer to your database"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Phone */}
              <div className="space-y-2">
                <Label>
                  Phone <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) handleFieldChange("phone", value);
                  }}
                  maxLength={10}
                  className={errors.phone ? "border-destructive" : ""}
                />
                {errors.phone && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label>
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="Enter customer name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  className={errors.name ? "border-destructive" : ""}
                  minLength={2}
                  maxLength={75}
                />
                {errors.name && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label>
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="email"
                  placeholder="customer@example.com"
                  value={formData.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.dateOfBirth || ""}
                    onChange={(e) =>
                      handleFieldChange("dateOfBirth", e.target.value)
                    }
                    className={errors.dateOfBirth ? "border-destructive" : ""}
                  />
                  {errors.dateOfBirth && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Date of Anniversary</Label>
                  <Input
                    type="date"
                    value={formData.dateOfAnniversary || ""}
                    onChange={(e) =>
                      handleFieldChange("dateOfAnniversary", e.target.value)
                    }
                    className={
                      errors.dateOfAnniversary ? "border-destructive" : ""
                    }
                  />
                  {errors.dateOfAnniversary && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.dateOfAnniversary}
                    </p>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label>
                  Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="Enter address"
                  value={formData.primaryAddress}
                  onChange={(e) =>
                    handleFieldChange("primaryAddress", e.target.value)
                  }
                  className={errors.primaryAddress ? "border-destructive" : ""}
                />
                {errors.primaryAddress && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.primaryAddress}
                  </p>
                )}
              </div>

              {/* Locality */}
              <div className="space-y-2">
                <Label>
                  Locality <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="Enter locality"
                  value={formData.primaryLocality}
                  onChange={(e) =>
                    handleFieldChange("primaryLocality", e.target.value)
                  }
                  className={errors.primaryLocality ? "border-destructive" : ""}
                />
                {errors.primaryLocality && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.primaryLocality}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* GST */}
                <div className="space-y-2">
                  <Label>GST No</Label>
                  <Input
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    value={formData.gstNo}
                    onChange={(e) =>
                      handleFieldChange("gstNo", e.target.value.toUpperCase())
                    }
                    maxLength={15}
                    className={errors.gstNo ? "border-destructive" : ""}
                  />
                  {errors.gstNo && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.gstNo}
                    </p>
                  )}
                </div>
                {/* Credit Limit */}
                <div className="space-y-2">
                  <Label>
                    Credit Limit <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.creditLimit}
                    onValueChange={(v) => handleFieldChange("creditLimit", v)}
                  >
                    <SelectTrigger
                      className={errors.creditLimit ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select limit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5000">5000</SelectItem>
                      <SelectItem value="10000">10000</SelectItem>
                      <SelectItem value="15000">15000</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.creditLimit && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.creditLimit}
                    </p>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>
                  Tag <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.tags}
                  onValueChange={(v) => handleFieldChange("tags", v)}
                >
                  <SelectTrigger
                    className={errors.tags ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder="Select tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Regular">Regular</SelectItem>
                    <SelectItem value="Vip">VIP</SelectItem>
                    <SelectItem value="Important">Important</SelectItem>
                  </SelectContent>
                </Select>
                {errors.tags && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.tags}
                  </p>
                )}
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 pt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.markAsFavorite}
                    onChange={(e) =>
                      handleFieldChange("markAsFavorite", e.target.checked)
                    }
                    className="w-4 h-4 rounded border-input"
                  />
                  <span className="text-sm font-medium text-foreground">
                    Mark As Favorite
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.doNotSendUpdates}
                    onChange={(e) =>
                      handleFieldChange("doNotSendUpdates", e.target.checked)
                    }
                    className="w-4 h-4 rounded border-input"
                  />
                  <span className="text-sm font-medium text-foreground">
                    Do Not Send Updates
                  </span>
                </label>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={showLoaderAdmin}
              >
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={showLoaderAdmin}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? (
                  "Saving..."
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editMode ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Customer</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete{" "}
                <strong>{selectedCustomer?.guest_name}</strong>? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={showLoaderAdmin}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={showLoaderAdmin}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
